<template>
  <v-col v-if="!showActivityDetails">
    <!-- Title -->
    <h1>{{ $translate('ACTIVITIES') }}</h1>

    <!-- Toggle button between activities and OrderGroups -->
    <v-row class="mt-2">
      <v-col cols="12" align="center">
        <v-btn-toggle v-model="activities_toggle" mandatory>
          <v-btn value="activities" class="text-capitalize">
            {{ $translate('ACTIVITIES') }}
          </v-btn>

          <v-btn value="orders" class="text-capitalize">
            {{ $translate('ORDERS') }}
          </v-btn>
        </v-btn-toggle>

        <!-- Error banner -->
        <v-slide-y-transition>
          <ErrorCard
            v-if="error"
            :errorMessage="errorMessage"
            :functionName="errorToRecallFunction"
            @getMyActivities="getMyActivities"
          />
        </v-slide-y-transition>

        <!-- Activities -->
        <template v-if="activities_toggle === 'activities'">
          <v-container v-if="activities.length > 0" class="mt-5">
            <v-row
              v-for="(activity, index) in activities"
              :key="index"
              justify="center"
              no-gutters
            >
              <v-col cols="12" md="10" lg="8">
                <ActivityTicket
                  :activity="activity"
                  @activity-details="openActivityDetails(activity)"
                  @reload="getMyActivities"
                />
              </v-col>
            </v-row>
          </v-container>
          <v-row v-else justify="center" class="ma-10 pt-5">
            <p class="text--disabled">
              {{ $translate('NO_UPCOMING_ACTIVITIES') }}
            </p>
          </v-row>
        </template>

        <!-- Orders -->
        <template v-else>
          <template v-if="OrderGroups.length > 0">
            <v-row class="d-flex d-md-none">
              <v-col>
                <v-autocomplete
                  :items="OrderGroups"
                  item-value="OrderGroupId"
                  :label="$translate('SEARCH_ORDERS')"
                  @input="getOrderGroupById"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-avatar>
                      <v-icon>mdi-clipboard-list-outline</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title
                        v-text="item.OrderGroupId.split('_')[2]"
                      />
                      <v-list-item-subtitle
                        v-text="unixTimeToDateTimeString(order.CreationTime)"
                      />
                      <v-list-item-subtitle
                        v-text="item.OrderGroupStatus"
                        :class="statusColor[item.OrderGroupStatus]"
                      />
                    </v-list-item-content>
                  </template>
                  <template v-slot:append-item>
                    <v-row class="mx-2 mt-2 mb-1">
                      <v-btn
                        color="primary"
                        block
                        class="primary-font-color"
                        :loading="loadingOrderGroups"
                        @click="getMyOrderGroups"
                      >
                        {{ $translate('LOAD_MORE') }}
                      </v-btn>
                    </v-row>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="3" class="d-none d-md-flex">
                <v-list height="800" style="overflow-y: auto" three-line>
                  <v-list-item-group :value="returnSelected()" color="primary">
                    <v-list-item
                      v-for="(order, index) in OrderGroups"
                      :key="index"
                      @click="getOrderGroupById(order.OrderGroupId)"
                    >
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ order.OrderGroupId.split('_')[2] }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          <div class="text-caption">
                            {{ unixTimeToDateTimeString(order.CreationTime) }}
                          </div>
                        </v-list-item-subtitle>
                        <v-list-item-subtitle
                          v-if="statusMsg[order.OrderGroupStatus] != null"
                          :class="statusColor[order.OrderGroupStatus]"
                          class="text-caption"
                        >
                          {{ $translate(statusMsg[order.OrderGroupStatus]) }}
                        </v-list-item-subtitle>
                        <v-list-item-subtitle v-else class="text-caption">
                          {{ order.OrderGroupStatus }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list-item-group>

                  <v-btn
                    color="primary"
                    block
                    class="primary-font-color mt-2"
                    :loading="loadingOrderGroups"
                    @click="getMyOrderGroups"
                  >
                    {{ $translate('LOAD_MORE') }}
                  </v-btn>
                </v-list>
              </v-col>
              <v-col
                cols="12"
                md="9"
                v-if="Object.keys(selectedOrderGroup).length !== 0"
              >
                <ActivityOrder
                  :order="selectedOrderGroup"
                  @reload="getMyOrderGroups"
                />
              </v-col>
            </v-row>
          </template>
          <v-row v-else justify="center" class="ma-10 pt-5">
            <p class="text--disabled">{{ $translate('NO_PAST_ACTIVITIES') }}</p>
          </v-row>
        </template>
      </v-col>
    </v-row>
  </v-col>

  <v-col v-else>
    <!-- Close button -->
    <v-btn text @click="closeActivityDetails">
      <h3>{{ $translate('CLOSE') }}</h3>
    </v-btn>

    <v-row justify="center">
      <v-col cols="10" align="center">
        <ActivityDetail :activity="selectedActivity" />
      </v-col>
    </v-row>
  </v-col>
</template>

<script>
import utility from '@/shared/utility';
import doRequest from '@/api/dssRequest';
import Activity from '@/objects/Activity';
import ErrorCard from '@/components/generalComponents/ErrorHandling/ErrorCard';
import ActivityTicket from './ActivityTicket.vue';
import ActivityOrder from './ActivityOrder.vue';
import ActivityDetail from './ActivityDetail.vue';

export default {
  data() {
    return {
      // ErrorCard variables
      error: false,
      errorMessage: this.$translate('LOADING_ACTIVITIES_FAILED'),
      errorToRecallFunction: 'getMyActivities',
      // End of ErrorCard variables
      statusColor: {
        Confirmed: 'success--text',
        PartnerConfirmation: 'warning--text',
        RequestedPayment: 'red--text',
        WaitingForPayment: 'red--text',
        Cancelled: 'red--text',
      },
      statusMsg: {
        Confirmed: 'STATUS_CONFIRMED',
        PartnerConfirmation: 'STATUS_PARTNER_CONFIRM',
        RequestedPayment: 'STATUS_REQUESTED_PAYMENT',
        WaitingForPayment: 'STATUS_REQUESTED_PAYMENT',
        Cancelled: 'CANCELED',
      },
      activities_toggle: 'activities',
      activities: [],
      OrderGroups: [],
      selectedOrderGroup: {},
      selectedActivity: {},
      showActivityDetails: false,
      loadingOrderGroups: false,
    };
  },
  components: {
    ActivityTicket,
    ActivityOrder,
    ActivityDetail,
    ErrorCard,
  },
  name: 'activities',
  watch: {
    activities_toggle(newV, oldV) {
      if (newV !== oldV) {
        if (newV === 'orders') {
          this.getMyOrderGroups();
        }
        window.EventBus.fire('activitiesToggle');
      }
    },
  },
  mounted() {
    this.getMyActivities();
    this.getMyOrderGroups();
  },
  methods: {
    returnSelected() {
      let index = -1;
      this.OrderGroups.forEach((order, orderIndex) => {
        if (
          `OG_${this.$store.getters['auth/userName']}_${this.selectedOrderGroup.OrderGroupId}` ===
          order.OrderGroupId
        ) {
          index = orderIndex;
        }
      });
      return index;
    },
    getMyActivities() {
      // Start with setting the error to false
      this.error = false;
      const payload = {};
      doRequest(
        (response) => {
          this.activities = [];
          const activitiesIn = response.data.Activities;

          if (activitiesIn != null && Object.keys(activitiesIn).length > 0) {
            Object.values(activitiesIn).forEach((item) => {
              const newActivity = Activity.parseActivity(item);
              if (newActivity.status !== 'Done') {
                this.activities.push(newActivity);
              }
            });
          }
        },
        () => {
          this.error = true;
        },
        'getMyActivities',
        this.$store.getters['auth/userName'],
        payload
      );
    },
    getOrderGroupById(id) {
      if (id == null || id.trim() === '') {
        return;
      }

      // Start with setting the error to false
      this.error = false;
      const payload = {
        OrderGroupId: id,
        IncludeTicket: true,
      };

      doRequest(
        (response) => {
          this.selectedOrderGroup = response.data.OrderGroup;
        },
        () => {
          this.error = true;
        },
        'getOrderGroupById',
        `${this.$store.getters['Account/partnerGroupDetails'].AccountId}_OM`,
        payload
      );
    },
    getMyOrderGroups() {
      // Start with setting the error to false
      this.error = false;
      this.loadingOrderGroups = true;

      const startPos = this.OrderGroups.length;
      const endPos = startPos + 10;

      const payload = {
        Start: startPos,
        End: endPos,
      };

      doRequest(
        (response) => {
          this.loadingOrderGroups = false;

          const orderGroupsIn = response.data.OrderGroups;

          if (orderGroupsIn != null && Object.keys(orderGroupsIn).length > 0) {
            orderGroupsIn.forEach((order) => {
              const index = this.OrderGroups.findIndex(
                (item) => item.OrderGroupId === order.OrderGroupId
              );
              if (index === -1) {
                this.OrderGroups.push(order);
              }
            });
          }

          // for (const orderGroup of this.OrderGroups) {
          //   orderGroup.FEdate = utility.unixTimeToDateTimeString(
          //     orderGroup.CreationTime
          //   );
          // }

          if (
            this.OrderGroups.length > 0 &&
            Object.keys(this.selectedOrderGroup).length === 0
          ) {
            [this.selectedOrderGroup] = this.OrderGroups;
            this.getOrderGroupById(this.selectedOrderGroup.OrderGroupId);
          }
        },
        () => {
          this.loadingOrderGroups = false;
          this.error = true;
        },
        'getMyOrderGroups',
        this.$store.getters['auth/userName'],
        payload
      );
    },
    openActivityDetails(activity) {
      this.selectedActivity = activity;
      this.showActivityDetails = true;
    },
    closeActivityDetails() {
      this.showActivityDetails = false;
    },
    unixTimeToDateTimeString(unixTime) {
      return utility.unixTimeToDateTimeString(unixTime);
    },
  },
};
</script>

<style scoped lang="scss">
a {
  text-decoration: inherit;
  color: inherit;
  cursor: auto;
}
</style>
